<template>
  <h5>{{ esAlta ? 'Alta de Usuario' : 'Modificar Usuario' }}</h5>

  <!-- Solo alta: guard de permiso -->
  <div v-if="esAlta && !puedeCrearUsuarios" class="alert alert-warning mt-3">
    No tienes permiso para crear usuarios.
  </div>

  <Form v-else role="form" class="text-start mt-4" :validation-schema="schema" @submit="handleSubmit">

    <soft-field id="username" type="text" v-model="user.username" :useLabel="true" label="Usuario" name="username" class="mb-2 label-input2" largo="30" />

    <soft-field id="nombre" type="text" v-model="user.name" :useLabel="true" label="Nombre" name="nombre" class="mb-2 label-input2" largo="80" />

    <soft-field id="email" type="email" v-model="user.email" :useLabel="true" label="Email" name="email" class="mb-2 label-input2" />

    <!-- Rol: alta usa user.rolid, edita usa rolUi con :disabled -->
    <soft-select
      v-if="esAlta"
      id="rolid"
      v-model="user.rolid"
      name="rolid"
      :valores="roles"
      :useLabel="true"
      label="Rol"
      class="mb-2 label-select2"
    />
    <soft-select
      v-if="esEdita"
      id="rolid"
      v-model="rolUi"
      name="rolid"
      :valores="roles"
      :useLabel="true"
      label="Rol"
      class="mb-2 label-select2"
      :disabled="!puedeEditarRol"
    />

    <!-- Perfil personalizado activo -->
    <div v-if="user.es_personalizado" class="text-xs text-muted mb-2">
      Perfil personalizado activo
      <a href="#" @click.prevent="abrirModalPermisos" class="ms-2">editar permisos</a>
      <a v-if="esEdita" href="#" @click.prevent="desactivarPersonalizado" class="ms-2">volver a rol normal</a>
    </div>

    <!-- Tipster (solo si rol=3) -->
    <soft-select
      v-if="esAlta && user.rolid === 3"
      id="tipsterid"
      v-model="user.tipsterid"
      name="tipsterid"
      :valores="tipsters"
      :useLabel="true"
      label="Tipster"
      class="mb-2 label-select2"
    />
    <soft-select
      v-if="esEdita && Number(user.rolid) === 3"
      id="tipsterid"
      v-model="user.tipsterid"
      name="tipsterid"
      :valores="tipsters"
      :useLabel="true"
      label="Tipster"
      class="mb-2 label-select2"
      :disabled="!puedeEditarTipster"
    />

    <!-- Botones -->
    <div class="q-form-footer">
      <button type="button" class="btn-q-secondary" @click="$emit('cerrar-modal')">
        Volver
      </button>
      <button type="submit" class="btn-q-primary" :disabled="loading">
        <span v-if="loading" class="spinner-border spinner-border-sm"></span>
        <span v-else>Confirmar</span>
      </button>
    </div>

  </Form>

  <!-- ===========================
       MODAL: PERFIL PERSONALIZADO
  =========================== -->
  <Teleport to="body">
  <div v-if="showPermModal" class="q-modal-backdrop" @click.self="cerrarModalPermisos">
    <div class="q-modal-card">
      <div class="q-modal-header">
        <h5 class="mb-0">Perfil personalizado</h5>
        <button type="button" class="btn-close" @click="cerrarModalPermisos"></button>
      </div>

      <div class="q-modal-body">
        <p class="text-sm mb-3">
          Selecciona permisos. <b>Heredar</b> usa el rol base. <b>Permitir</b> fuerza acceso, <b>Denegar</b> lo bloquea.
        </p>

        <soft-select
          id="rolbase"
          v-model="rolBase"
          name="rolbase"
          :valores="rolesBase"
          :useLabel="true"
          label="Rol base"
          class="mb-3 label-select2"
        />

        <div v-for="grupo in gruposPermisos" :key="grupo" class="q-perm-group">
          <div class="q-perm-title">{{ grupo }}</div>

          <div class="q-perm-row" v-for="p in permisosPorGrupo(grupo)" :key="p.clave">
            <div class="q-perm-name">
              <div class="fw-semibold">{{ p.nombre }}</div>
              <div v-if="rolBaseNombre" :class="rolBaseIncluye(p) ? 'q-perm-status--yes' : 'q-perm-status--no'">
                {{ rolBaseIncluye(p) ? '✓ Incluido en ' + rolBaseNombre : '✗ No incluido en ' + rolBaseNombre }}
              </div>
            </div>

            <div class="q-perm-actions">
              <label class="q-radio">
                <input type="radio" :name="'perm_'+p.clave" value="inherit" v-model="permState[p.clave]" />
                <span>Heredar</span>
              </label>
              <label class="q-radio">
                <input type="radio" :name="'perm_'+p.clave" value="allow" v-model="permState[p.clave]" />
                <span>Permitir</span>
              </label>
              <label class="q-radio">
                <input type="radio" :name="'perm_'+p.clave" value="deny" v-model="permState[p.clave]" />
                <span>Denegar</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <div class="q-modal-footer">
        <button type="button" class="btn btn-outline-secondary" @click="cerrarModalPermisos">
          Cancelar
        </button>
        <button type="button" class="btn btn-primary" @click="aplicarPermisosPersonalizados">
          Guardar
        </button>
      </div>
    </div>
  </div>
  </Teleport>
</template>

<script>
import SoftField from "@/components/SoftField.vue";
import SoftSelect from "@/components/SoftSelect.vue";
import showSwal from "/src/mixins/showSwal.js";
import { Form } from "vee-validate";
import * as yup from "yup";

export default {
  name: "UsuarioForm",
  components: { SoftField, SoftSelect, Form },

  props: {
    modo: { type: String, required: true }, // 'alta' | 'edita'
    // Edita only (todos opcionales):
    usuarioId: Number,
    usuarioName: String,
    usuarioUsername: String,
    usuarioEmail: String,
    usuarioRolId: [String, Number],
    usuarioTipsterId: [String, Number],
    usuarioEsPersonalizado: [Boolean, Number],
    usuarioRolBaseId: [String, Number],
    usuarioPermisosAllow: { type: Array, default: () => [] },
    usuarioPermisosDeny: { type: Array, default: () => [] },
  },

  emits: ["cerrar-modal"],

  data() {
    const schema = yup.object().shape({
      email: yup.string().required("El email es necesario!").email("Debe indicar un email válido"),
      username: yup
        .string()
        .required("El usuario es necesario!")
        .max(30, "El tamaño máximo es de 30 caracteres")
        .matches(/^[a-zA-Z0-9.-]+$/, "El usuario contiene caracteres no permitidos"),
      nombre: yup.string().required("El nombre es necesario!").max(80, "El tamaño máximo es de 80 caracteres"),
      rolid: yup.number().positive("Debe indicar un rol"),
      tipsterid: yup
        .number()
        .nullable()
        .when("rolid", {
          is: 3,
          then: (s) => s.required("Debe seleccionar un tipster").positive("Debe seleccionar un tipster"),
          otherwise: (s) => s.nullable(),
        }),
    });

    const isEdita = this.modo === "edita";

    return {
      loading: false,
      schema,
      tipsters: [],
      roles: [],
      rolesBase: [],
      rolUi: 0,

      user: {
        id: isEdita ? this.usuarioId : undefined,
        username: isEdita ? (this.usuarioUsername || "") : "",
        name: isEdita ? (this.usuarioName || "") : "",
        email: isEdita ? (this.usuarioEmail || "") : "",
        rolid: isEdita ? (Number(this.usuarioRolId || 0) || null) : null,
        tipsterid: isEdita ? (Number(this.usuarioTipsterId || 0) || null) : null,
        rol_base_id: isEdita ? (Number(this.usuarioRolBaseId || 0) || null) : null,
        es_personalizado: isEdita ? (Number(this.usuarioEsPersonalizado) ? 1 : 0) : 0,
        permisos_allow: isEdita ? (Array.isArray(this.usuarioPermisosAllow) ? this.usuarioPermisosAllow : []) : [],
        permisos_deny: isEdita ? (Array.isArray(this.usuarioPermisosDeny) ? this.usuarioPermisosDeny : []) : [],
      },

      // Modal permisos
      showPermModal: false,
      rolBase: isEdita ? (Number(this.usuarioRolBaseId || 0) || 3) : 3,
      permState: {},

      permisosCatalogo: [],
    };
  },

  computed: {
    esAlta() { return this.modo === "alta"; },
    esEdita() { return this.modo === "edita"; },
    miRol() { return this.$store.getters["auth/rolEfectivo"]; },
    miTipsterId() { return Number(localStorage.getItem("usuarioTipsterId") || 0); },

    gruposPermisos() {
      return Array.from(new Set(this.permisosCatalogo.map((p) => p.grupo)));
    },

    rolBaseNombre() {
      const rol = (this.rolesBase || []).find((r) => Number(r.id) === Number(this.rolBase));
      return rol ? rol.nombre : null;
    },

    // Solo alta
    puedeCrearUsuarios() {
      const raw = localStorage.getItem("permisos_finales");
      if (raw) {
        try {
          const arr = JSON.parse(raw);
          return Array.isArray(arr) && arr.includes("usuarios.crear");
        } catch (e) {
          return Number(this.miRol) === 1;
        }
      }
      return Number(this.miRol) === 1;
    },

    // Solo edita
    puedeEditarRol() { return Number(this.miRol) === 1; },
    puedeEditarTipster() { return Number(this.miRol) === 1; },
  },

  watch: {
    // Alta: watch user.rolid directamente
    "user.rolid"(nuevo) {
      if (!this.esAlta) return;

      if (Number(nuevo) === 4) {
        showSwal.methods.showSwal({
          type: "warning",
          message: "Para crear este tipo de usuario diríjase a la opción Tipster del menú lateral.",
          width: 650,
        });
        this.user.rolid = 3;
        return;
      }

      if (Number(nuevo) !== 3) this.user.tipsterid = null;

      if (Number(nuevo) === 999) {
        const fallbackBase = this.rolesBase?.[0]?.id || 3;
        this.rolBase = Number(this.rolBase || fallbackBase);
        this.user.rolid = Number(this.rolBase);
        this.abrirModalPermisos();
      }
    },

    // Edita: watch rolUi
    rolUi(nuevo) {
      if (!this.esEdita) return;
      const r = Number(nuevo || 0);

      if (r === 999 && Number(this.miRol) === 1) {
        this.activarPersonalizado();
        return;
      }

      this.user.es_personalizado = 0;
      this.user.rol_base_id = null;
      this.user.permisos_allow = [];
      this.user.permisos_deny = [];
      this.user.rolid = r;

      if (r !== 3) this.user.tipsterid = null;
    },

    // Edita: sync cuando cambia el usuario
    usuarioId() {
      if (!this.esEdita) return;
      this.syncFromProps();
    },
  },

  methods: {
    permisosPorGrupo(grupo) {
      return this.permisosCatalogo.filter((p) => p.grupo === grupo);
    },

    rolBaseIncluye(permiso) {
      if (!this.rolBaseNombre) return false;
      return (permiso.roles || []).includes(this.rolBaseNombre);
    },

    // Edita only
    setRolUiFromUser() {
      this.rolUi = this.user.es_personalizado ? 999 : Number(this.user.rolid || 0);
    },

    activarPersonalizado() {
      const baseIds = new Set((this.rolesBase || []).map((r) => Number(r.id)));
      const current = Number(this.user.rolid || 0);
      const base = baseIds.has(current) ? current : Number(this.rolesBase?.[0]?.id || 3);

      this.user.es_personalizado = 1;
      this.user.rol_base_id = base;
      this.rolBase = base;
      this.user.rolid = base;
      this.rolUi = 999;

      this.abrirModalPermisos();
    },

    desactivarPersonalizado() {
      this.user.es_personalizado = 0;
      this.user.rol_base_id = null;
      this.user.permisos_allow = [];
      this.user.permisos_deny = [];
      this.rolUi = Number(this.user.rolid || 0);
    },

    syncFromProps() {
      this.user.id = this.usuarioId;
      this.user.username = this.usuarioUsername || "";
      this.user.name = this.usuarioName || "";
      this.user.email = this.usuarioEmail || "";
      this.user.rolid = Number(this.usuarioRolId || 0) || null;
      this.user.tipsterid = Number(this.usuarioTipsterId || 0) || null;
      this.user.es_personalizado = Number(this.usuarioEsPersonalizado) ? 1 : 0;
      this.user.rol_base_id = Number(this.usuarioRolBaseId || 0) || null;
      this.user.permisos_allow = Array.isArray(this.usuarioPermisosAllow) ? this.usuarioPermisosAllow : [];
      this.user.permisos_deny = Array.isArray(this.usuarioPermisosDeny) ? this.usuarioPermisosDeny : [];
      this.rolBase = Number(this.user.rol_base_id || this.user.rolid || 3);
      this.setRolUiFromUser();
    },

    // Shared — versión más completa (Edita) que precarga allow/deny
    abrirModalPermisos() {
      const state = {};
      for (const p of this.permisosCatalogo) state[p.clave] = "inherit";
      // precarga allow/deny (en alta estarán vacíos)
      for (const clave of this.user.permisos_allow || []) state[clave] = "allow";
      for (const clave of this.user.permisos_deny || []) state[clave] = "deny";
      this.permState = state;

      const baseIds = new Set((this.rolesBase || []).map((r) => Number(r.id)));
      const current = Number(this.user.rolid || 0);
      const safeBase = baseIds.has(current) ? current : Number(this.rolesBase?.[0]?.id || 3);

      this.rolBase = Number(this.user.rol_base_id || safeBase);
      this.user.rolid = Number(this.rolBase);
      this.user.es_personalizado = 1;
      this.showPermModal = true;
    },

    cerrarModalPermisos() {
      this.showPermModal = false;
      if (this.esEdita) this.rolUi = 999;
    },

    aplicarPermisosPersonalizados() {
      const allow = [];
      const deny = [];
      Object.entries(this.permState).forEach(([clave, v]) => {
        if (v === "allow") allow.push(clave);
        if (v === "deny") deny.push(clave);
      });
      this.user.rolid = Number(this.rolBase || 3);
      this.user.rol_base_id = Number(this.rolBase || 3);
      this.user.permisos_allow = allow;
      this.user.permisos_deny = deny;
      this.user.es_personalizado = 1;
      this.showPermModal = false;
      if (this.esEdita) this.rolUi = 999;
    },

    // Submit dispatcher
    async handleSubmit() {
      if (this.esAlta) {
        await this.handleAlta();
      } else {
        await this.handleEdita();
      }
    },

    async handleAlta() {
      if (!this.puedeCrearUsuarios) {
        showSwal.methods.showSwal({ type: "error", message: "No tienes permiso para crear usuarios.", width: 500 });
        return;
      }
      this.loading = true;
      try {
        this.loading = false;
        const resp = await this.$store.dispatch("auth/guardaUsuario", this.user);
        if (resp.res === "OK") {
          showSwal.methods.showSwal({ type: "success", message: "Se ha registrado el nuevo usuario", width: 600 });
          this.$emit("cerrar-modal");
        } else {
          showSwal.methods.showSwal({ type: "error", message: resp.mensaje, width: 500 });
        }
      } catch (e) {
        this.loading = false;
        showSwal.methods.showSwal({ type: "error", message: "Se ha producido un error", width: 500 });
      }
    },

    async handleEdita() {
      this.loading = true;
      try {
        if (Number(this.miRol) !== 1) {
          const rol = Number(this.user.rolid || 0);
          if (![2, 3].includes(rol)) this.user.rolid = 3;
          this.user.tipsterid = Number(this.miTipsterId || 0) || null;
          this.user.es_personalizado = 0;
          this.user.rol_base_id = null;
          this.user.permisos_allow = [];
          this.user.permisos_deny = [];
        }
        const payload = {
          id: this.user.id,
          name: this.user.name,
          username: this.user.username,
          email: this.user.email,
          rolid: Number(this.user.rolid || 0),
          tipsterid: this.user.tipsterid ? Number(this.user.tipsterid) : null,
          es_personalizado: this.user.es_personalizado ? 1 : 0,
          rol_base_id: this.user.rol_base_id ? Number(this.user.rol_base_id) : null,
          permisos_allow: Array.isArray(this.user.permisos_allow) ? this.user.permisos_allow : [],
          permisos_deny: Array.isArray(this.user.permisos_deny) ? this.user.permisos_deny : [],
        };
        const resp = await this.$store.dispatch("auth/guardaUsuario", payload);
        if (resp?.res === "OK") {
          showSwal.methods.showSwal({ type: "success", message: "Se ha actualizado el usuario", width: 600 });
          this.$emit("cerrar-modal");
        } else {
          showSwal.methods.showSwal({ type: "error", message: resp?.mensaje || "No se pudo guardar", width: 500 });
        }
      } catch (e) {
        showSwal.methods.showSwal({ type: "error", message: "Se ha producido un error", width: 500 });
      } finally {
        this.loading = false;
      }
    },
  },

  async mounted() {
    this.tipsters = await this.$store.dispatch("auth/listaTipsters");

    if (this.esAlta && !this.puedeCrearUsuarios) return;

    if (Number(this.miRol) === 1) {
      const resp = await this.$store.dispatch("auth/listaRoles");
      if (resp?.res === "OK" && Array.isArray(resp.roles)) {
        // Alta: [2,3,5] — Edita: [2,3,4,5]
        const creatableIds = new Set(this.esAlta ? [2, 3, 5] : [1, 2, 3, 4, 5]);
        const base = resp.roles.filter((r) => creatableIds.has(Number(r.id)));
        this.rolesBase = base;
        this.roles = [...base, { id: 999, nombre: "Personalizado" }];
      } else {
        this.rolesBase = this.esAlta
          ? [{ id: 2, nombre: "Admin.Tipster" }, { id: 3, nombre: "Cliente" }, { id: 5, nombre: "Publicista" }]
          : [{ id: 1, nombre: "Administrador" }, { id: 2, nombre: "Admin.Tipster" }, { id: 3, nombre: "Cliente" }, { id: 4, nombre: "Tipster" }, { id: 5, nombre: "Publicista" }];
        this.roles = [...this.rolesBase, { id: 999, nombre: "Personalizado" }];
      }
    } else {
      this.rolesBase = [];
      this.roles = [{ id: 2, nombre: "Admin.Tipster" }, { id: 3, nombre: "Cliente" }];
    }

    // Cargar catálogo de permisos desde la API
    try {
      const permisos = await this.$store.dispatch("auth/listaPermisos");
      if (Array.isArray(permisos)) {
        this.permisosCatalogo = permisos.map((p) => ({
          grupo: p.grupo || "General",
          clave: p.clave,
          nombre: p.nombre,
          roles: typeof p.roles === "string" ? p.roles.split(", ").map((r) => r.trim()) : [],
        }));
      }
    } catch (e) {
      // Si falla (sin permiso permisos.ver), el modal mostrará lista vacía sin romper
    }

    if (this.esAlta) {
      if (!this.user.rolid) {
        this.user.rolid = this.rolesBase?.[0]?.id || 3;
        this.rolBase = this.user.rolid;
      }
    } else {
      // edita
      this.user.rolid = Number(this.user.rolid || 0) || null;
      this.user.tipsterid = this.user.tipsterid ? Number(this.user.tipsterid) : null;
      this.rolBase = Number(this.user.rol_base_id || this.user.rolid || 3);
      this.setRolUiFromUser();
    }
  },
};
</script>

<style scoped>
.q-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, .45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200000;
  padding: 16px;
}

.q-modal-card {
  width: 100%;
  max-width: 640px;
  max-height: 90vh;
  background: var(--q-card);
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, .25);
  display: flex;
  flex-direction: column;
}

.q-modal-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid var(--q-border);
}

.q-modal-body {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
}

.q-modal-footer {
  flex-shrink: 0;
  padding: 12px 16px;
  border-top: 1px solid var(--q-border);
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.q-perm-status--yes {
  display: inline-block;
  font-size: 0.68rem;
  font-weight: 600;
  color: var(--q-green);
  background: var(--q-green-soft);
  border: 1px solid var(--q-green);
  border-radius: 20px;
  padding: 1px 8px;
  margin-top: 3px;
}

.q-perm-status--no {
  display: inline-block;
  font-size: 0.68rem;
  font-weight: 500;
  color: var(--q-muted);
  background: var(--q-hover);
  border: 1px solid var(--q-border);
  border-radius: 20px;
  padding: 1px 8px;
  margin-top: 3px;
}

.q-perm-title {
  font-weight: 700;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--q-gold);
  background: var(--q-card-alt);
  border-left: 3px solid var(--q-gold);
  padding: 6px 10px;
  border-radius: 0 6px 6px 0;
  margin-bottom: 6px;
  margin-top: 12px;
}

.q-perm-row {
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
  border-top: 1px dashed var(--q-border);
}

.q-perm-row:first-of-type {
  border-top: none;
}

.q-perm-name {
  min-width: min(280px, 100%);
  flex: 1;
}

.q-perm-actions {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.q-radio {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9rem;
}

.q-radio input {
  transform: translateY(1px);
}

/* ===== BOTONES FORM ===== */
.q-form-footer {
  width: 100%;
  box-sizing: border-box;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 14px;
  padding-top: 16px;
  margin-top: 18px;
  border-top: 1px solid var(--q-border);
}

.q-flex-between {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.q-mar-top {
  margin-top: 1rem;
}

.btn-q-primary,
.btn-q-secondary {
  min-width: 180px;
  height: 48px;
  padding: 0 24px;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 10px;
  border: none;
  cursor: pointer;
}

.btn-q-primary {
  background: var(--q-gold);
  color: var(--q-gold-on);
}

.btn-q-secondary {
  background: var(--q-hover);
  color: var(--q-muted);
}

/* Mobile */
@media (max-width: 768px) {
  .q-flex-between {
    flex-direction: column;
    align-items: stretch;
  }

  .q-perm-row {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 8px;
  }

  .q-perm-actions {
    justify-content: flex-start !important;
  }

  .q-modal-card {
    max-width: 95% !important;
  }

  .btn-q-primary,
  .btn-q-secondary {
    width: 100%;
    min-width: unset;
  }

  .q-form-footer {
    flex-direction: column;
    align-items: stretch;
  }

  .q-form-footer .btn-q-primary,
  .q-form-footer .btn-q-secondary {
    width: 100%;
    min-width: 0;
  }

  .q-actions {
    position: sticky;
    bottom: 0;
    background: var(--q-card);
    padding: 12px 0 calc(12px + env(safe-area-inset-bottom));
    border-top: 1px solid var(--q-border);
    z-index: 3;
  }
}
</style>
